import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, ScrollView, ActivityIndicator, TextInput } from 'react-native';
import { Image } from 'expo-image';
import { useLocalSearchParams } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import Toast from 'react-native-toast-message';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import {
    getPublicGivingStatus,
    createPayinConfig,
    createPayinConfigUnauthenticated,
    getPaymentUrl,
    getLocalPaymentMethods,
    saveLocalPaymentMethod,
    pollPayinStatus,
    calculateGivingCharge,
    getGivingFeeNotice,
} from '@/data/giving';
import GivingHero from '@/components/Giving/GivingHero';
import GivingCard from '@/components/Giving/GivingCard';
import FundSplitsEditor from '@/components/Giving/FundSplitsEditor';
import NoteInput from '@/components/Giving/NoteInput';
import PaymentMethodPicker from '@/components/Giving/PaymentMethodPicker';
import AddPaymentMethodModal from '@/components/Giving/AddPaymentMethodModal';
import PaymentWebView from '@/components/Giving/PaymentWebView';
import GivingReceipt from '@/components/Giving/GivingReceipt';
import SolidButton from '@/components/General/SolidButton';
import Unicon from '@/components/General/Unicon';
import { PatternBackground } from '@/components/General/Backgrounds';
import { isPaymentMethodUsable, preferredPaymentMethodID } from '@/utils/paymentMethodStatus';
import FeeCoverageToggle from '@/components/Giving/FeeCoverageToggle';
import { getAccessibleTextColor } from '@/utils/colorAccessibility';

const mixHex = (color, target, amount) => {
    const value = color.replace('#', '');
    const targetValue = target.replace('#', '');
    const channels = [0, 2, 4].map((offset) => {
        const start = parseInt(value.slice(offset, offset + 2), 16);
        const end = parseInt(targetValue.slice(offset, offset + 2), 16);
        return Math.round(start + ((end - start) * amount)).toString(16).padStart(2, '0');
    });
    return `#${channels.join('')}`;
};

export default function PublicGiveScreen() {
    const { orgHandle } = useLocalSearchParams();
    const queryClient = useQueryClient();
    const { theme: { colors, scheme } } = useUnistyles();

    const [status, setStatus] = useState(null);
    const [funds, setFunds] = useState([]);
    const [localMethods, setLocalMethods] = useState([]);
    const [loading, setLoading] = useState(true);

    const [amountCents, setAmountCents] = useState(0);
    const [fundSplits, setFundSplits] = useState([]);
    const [note, setNote] = useState('');
    const [selectedPaymentMethodID, setSelectedPaymentMethodID] = useState(null);
    const [billingName, setBillingName] = useState('');
    const [billingEmail, setBillingEmail] = useState('');
    const [billingPostal, setBillingPostal] = useState('');

    const [paymentUrl, setPaymentUrl] = useState(null);
    const [payinConfigID, setPayinConfigID] = useState(null);
    const [paymentError, setPaymentError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [paymentTimedOut, setPaymentTimedOut] = useState(false);
    const [creating, setCreating] = useState(false);
    const [receiptData, setReceiptData] = useState(null);
    const [showAddMethod, setShowAddMethod] = useState(false);
    const [chargeSummary, setChargeSummary] = useState(null);
    const [coverFees, setCoverFees] = useState(true);
    const [reviewedCharge, setReviewedCharge] = useState(null);
    const effectiveCoverFees = !status?.allowGiversToChooseCoverFees || coverFees;

    // Detect logged-in Reach user — their DB payment methods are loaded if they
    // belong to this org. If logged in but different org, they're treated as a
    // public giver (unauthenticated edge function calls).
    const { data: loggedInUser } = Query.people.loggedInUser();
    const personID = loggedInUser?.id;
    const statusOrgID = status?.organizationID;
    const isOwnOrg = !!(loggedInUser && statusOrgID && loggedInUser.organizationID === statusOrgID);
    const { data: dbPaymentMethods } = Query.giving.paymentMethods({
        organizationID: statusOrgID,
        personID,
        options: { enabled: isOwnOrg },
    });

    // Merge DB methods (logged-in user's own org) with locally stored methods.
    // DB methods take priority; local methods fill in for devices/browsers where
    // the user gave publicly before. Dedup by paymentMethodID.
    const paymentMethods = useMemo(() => {
        const db = (isOwnOrg ? dbPaymentMethods : null) || [];
        const local = localMethods || [];
        const seen = new Set();
        const merged = [];
        for (const m of [...db, ...local]) {
            const id = m.paymentMethodID || m.id;
            if (id && !seen.has(id)) {
                seen.add(id);
                merged.push(m);
            }
        }
        return merged;
    }, [isOwnOrg, dbPaymentMethods, localMethods]);

    const selectedPaymentMethod = useMemo(() =>
        paymentMethods.find((method) => (method.paymentMethodID || method.id) === selectedPaymentMethodID) || null,
    [paymentMethods, selectedPaymentMethodID]);
    const feeSummary = status?.feeSummary || null;
    const localCharge = useMemo(() =>
        calculateGivingCharge(amountCents, selectedPaymentMethod, feeSummary, effectiveCoverFees),
    [amountCents, selectedPaymentMethod, feeSummary, effectiveCoverFees]);
    const calculatedCharge = isPaymentMethodUsable(selectedPaymentMethod) ? reviewedCharge || localCharge : null;
    const feeNotice = useMemo(() =>
        effectiveCoverFees ? getGivingFeeNotice(selectedPaymentMethod, feeSummary, status?.name, status?.allowGiversToChooseCoverFees) : null,
    [selectedPaymentMethod, feeSummary, status?.name, status?.allowGiversToChooseCoverFees, effectiveCoverFees]);
    const billingContact = useMemo(() => ({
        name: billingName,
        email: billingEmail,
        ...(billingPostal ? { postal_code: billingPostal } : {}),
    }), [billingName, billingEmail, billingPostal]);
    const appearance = useMemo(() => {
        const saved = status?.appearance || {};
        const baseColor = saved.baseColor || colors.giving;
        const backgroundColor = saved.backgroundColor || colors.background;
        const surfaceColor = saved.surfaceColor || colors.surface;
        const textColor = saved.textColor || colors.text;
        return {
            baseColor,
            backgroundColor,
            surfaceColor,
            textColor,
            logoPosition: saved.logoPosition || 'square',
            logoStyle: saved.logoStyle || 'light',
            gradientColors: [mixHex(baseColor, '#FFFFFF', 0.04), mixHex(baseColor, '#000000', 0.2)],
            heroTextColor: getAccessibleTextColor(mixHex(baseColor, '#000000', 0.1)),
            buttonTextColor: getAccessibleTextColor(baseColor),
            inputColor: mixHex(surfaceColor, textColor, 0.07),
            baseContainerColor: mixHex(surfaceColor, baseColor, 0.14),
        };
    }, [status?.appearance, colors]);
    const logoUrl = appearance.logoPosition === 'long'
        ? (appearance.logoStyle === 'dark'
            ? (status?.logoPublicUrlLongDark || status?.logoPublicUrlLong)
            : (status?.logoPublicUrlLong || status?.logoPublicUrlLongDark))
        : (appearance.logoStyle === 'dark'
            ? (status?.logoPublicUrlDark || status?.logoPublicUrl)
            : (status?.logoPublicUrl || status?.logoPublicUrlDark));

    // Preselect the first saved payment method.
    useEffect(() => {
        if (!paymentMethods.length) return;
        const nextID = preferredPaymentMethodID(paymentMethods, selectedPaymentMethodID);
        if (nextID !== selectedPaymentMethodID) {
            setSelectedPaymentMethodID(nextID);
            setReviewedCharge(null);
        }
    }, [paymentMethods, selectedPaymentMethodID]);

    // Pre-fill billing contact from logged-in user profile (own org only).
    useEffect(() => {
        if (!isOwnOrg || !loggedInUser) return;
        if (!billingName) {
            const name = `${loggedInUser.firstName || ''} ${loggedInUser.lastName || ''}`.trim();
            if (name) setBillingName(name);
        }
        if (!billingEmail && loggedInUser.email) setBillingEmail(loggedInUser.email);
    }, [isOwnOrg, loggedInUser, billingName, billingEmail]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const givingStatus = await getPublicGivingStatus(orgHandle);
                if (cancelled) return;
                setStatus(givingStatus);

                setFunds(givingStatus?.funds || []);

                const saved = await getLocalPaymentMethods(orgHandle);
                setLocalMethods(saved);
            } catch (error) {
                Toast.show({ type: 'error', text2: error?.message || 'Failed to load giving page' });
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [orgHandle]);

    useEffect(() => {
        if (!funds.length || fundSplits.length > 0) return;
        const defaultFund = funds.find((f) => f.isDefault) || funds[0];
        setFundSplits([{ fundID: defaultFund?.id, amount: amountCents }]);
    }, [funds, fundSplits.length, amountCents]);

    const handleAmountChange = useCallback((cents) => {
        setAmountCents(cents);
        setReviewedCharge(null);
        setFundSplits((prev) => (prev.length === 1 ? [{ ...prev[0], amount: cents }] : prev));
    }, []);

    const totalSplitCents = useMemo(() => fundSplits.reduce((sum, row) => sum + (row.amount || 0), 0), [fundSplits]);
    const hasAmount = amountCents > 0;
    const isBalanced = totalSplitCents === amountCents && hasAmount;

    const formatCurrency = (cents) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency: feeSummary?.currencyCode || 'USD', minimumFractionDigits: 2 }).format(cents / 100);

    const submitButtonText = useMemo(() => {
        return `Give ${formatCurrency(calculatedCharge?.grossAmount ?? amountCents)}`;
    }, [amountCents, calculatedCharge]);

    const givingDisabledReason = !hasAmount
        ? 'Enter an amount to give.'
        : !isBalanced
            ? 'Fund allocations must equal the gift amount.'
            : !selectedPaymentMethod
                ? 'Add and select a saved payment method before giving.'
                : !feeSummary
                    ? 'Online fee details are unavailable. Try again later.'
                    : !calculatedCharge
                        ? 'Fee details for this payment method are unavailable.'
                        : null;

    // Give with a saved payment method: create the payin config on press and
    // show the autoSubmit payment screen. Uses the authenticated edge function
    // path when the user is logged in and giving to their own org; otherwise
    // falls back to the public (unauthenticated) path.
    const handleGive = useCallback(async () => {
        setPaymentError(null);
        if (!isBalanced || !orgHandle || !selectedPaymentMethodID || !calculatedCharge) return;

        const payload = isOwnOrg
            ? {
                organizationID: statusOrgID,
                amount: amountCents,
                fundSplits,
                note,
                personID,
                paymentMethodID: selectedPaymentMethodID,
                billingContact,
                coverFees: effectiveCoverFees,
                expectedGrossAmount: calculatedCharge.grossAmount,
            }
            : {
                orgHandle,
                amount: amountCents,
                fundSplits,
                note,
                paymentMethodID: selectedPaymentMethodID,
                billingContact,
                coverFees: effectiveCoverFees,
                expectedGrossAmount: calculatedCharge.grossAmount,
            };

        setCreating(true);
        try {
            const config = isOwnOrg
                ? await createPayinConfig(payload)
                : await createPayinConfigUnauthenticated(payload);
            if (config.confirmationRequired) {
                setReviewedCharge(config);
                setCoverFees(config.coverFees);
                setStatus((current) => ({ ...current, allowGiversToChooseCoverFees: config.allowGiversToChooseCoverFees }));
                Toast.show({ type: 'info', text2: 'Payment total changed. Review the total and press Give to confirm.' });
                return;
            }
            if (!Number.isInteger(config.grossAmount) || !Number.isInteger(config.convenienceFee)) {
                throw new Error('Payment fee details were unavailable');
            }
            setChargeSummary({
                grossAmount: config.grossAmount,
                convenienceFee: config.convenienceFee,
                methodType: config.methodType,
            });
            setPayinConfigID(config.payinConfigID);
            const url = getPaymentUrl({
                sessionKey: config.sessionKey,
                payinConfigID: config.payinConfigID,
                paymentMethodID: selectedPaymentMethodID,
                autoSubmit: !!selectedPaymentMethodID,
                theme: scheme === 'dark' ? 'dark' : 'light',
                bg: appearance.backgroundColor,
            });
            setPaymentUrl(url);
        } catch (error) {
            Toast.show({ type: 'error', text2: error?.message || 'Failed to start payment' });
        } finally {
            setCreating(false);
        }
    }, [isBalanced, orgHandle, isOwnOrg, statusOrgID, personID, amountCents, fundSplits, note, selectedPaymentMethodID, calculatedCharge, effectiveCoverFees, billingContact, scheme, appearance.backgroundColor]);

    const handleSelectPaymentMethod = useCallback((paymentMethodID) => {
        setSelectedPaymentMethodID(paymentMethodID);
        setReviewedCharge(null);
        setPaymentError(null);
    }, []);

    const handlePaymentMethodAdded = useCallback(async (method) => {
        await saveLocalPaymentMethod(orgHandle, method);
        setLocalMethods((current) => [
            method,
            ...current.filter((item) => (item.paymentMethodID || item.id) !== method.paymentMethodID),
        ]);
        setSelectedPaymentMethodID(method.paymentMethodID);
        setReviewedCharge(null);
    }, [orgHandle]);

    // Poll GivingPayins for the payin record once the payment form is shown.
    useEffect(() => {
        if (!paymentUrl || !payinConfigID) return;
        let cancelled = false;

        setProcessing(true);

        const poll = async () => {
            try {
                const payin = await pollPayinStatus(payinConfigID, { intervalMs: 2000, timeoutMs: 120000, authenticated: isOwnOrg });
                if (cancelled) return;
                if (payin) {
                    handlePayinFound(payin);
                } else {
                    // Timeout — the payment may still be processing server-side.
                    // Swap the dead payment view for a "still processing" notice.
                    console.warn('[public-give] poll timeout — payin not found within 120s');
                    setPaymentUrl(null);
                    setPayinConfigID(null);
                    setProcessing(false);
                    setPaymentTimedOut(true);
                }
            } catch (err) {
                if (cancelled) return;
                console.error('[public-give] poll error:', err);
                setPaymentUrl(null);
                setPayinConfigID(null);
                setProcessing(false);
                setPaymentTimedOut(true);
            }
        };
        poll();

        return () => { cancelled = true; };
    }, [paymentUrl, payinConfigID]);

    const handlePayinFound = useCallback(async (payin) => {
        const raw = payin.rawPayload || {};
        const card = payin.card || raw.card || raw.payment_method?.card || null;
        const ach = payin.ach || raw.ach || raw.payment_method?.ach || null;
        const applePay = payin.applePay || raw.apple_pay || raw.payment_method?.apple_pay || null;

        const fundSplitsForReceipt = fundSplits.map((s) => {
            const fund = funds?.find((f) => f.id === s.fundID);
            return {
                fundID: s.fundID,
                fundName: fund?.name || 'General',
                amountCents: s.amount,
            };
        });
        setReceiptData({
            payinID: payin.payinID || 'N/A',
            amountCents,
            convenienceFeeCents: chargeSummary?.convenienceFee,
            grossAmountCents: chargeSummary?.grossAmount,
            currencyCode: feeSummary?.currencyCode || 'USD',
            methodType: chargeSummary?.methodType || payin.methodType || selectedPaymentMethod?.methodType,
            fundSplits: fundSplitsForReceipt,
            note,
            billingContact: {
                name: billingName,
                email: billingEmail,
            },
            card: card || (selectedPaymentMethod?.cardBrand ? { brand: selectedPaymentMethod.cardBrand, last_4: selectedPaymentMethod.cardLast4 } : null),
            ach: ach || (selectedPaymentMethod?.achAccountNumberLast4 ? { bank_name: selectedPaymentMethod.achBankName, account_number_last_4: selectedPaymentMethod.achAccountNumberLast4 } : null),
            applePay: applePay || ((selectedPaymentMethod?.methodType || '').toUpperCase() === 'APPLE_PAY'
                ? { description: [selectedPaymentMethod.cardBrand, selectedPaymentMethod.cardLast4].filter(Boolean).join(' ') || null }
                : null),
            date: payin.createdAt || raw.created_at || new Date().toISOString(),
        });

        if (isOwnOrg) {
            queryClient.invalidateQueries({ queryKey: QueryKeys.giving.paymentMethods(statusOrgID, personID) });
        }
        setPaymentUrl(null);
        setPayinConfigID(null);
        setProcessing(false);
        setSuccess(true);
    }, [isOwnOrg, statusOrgID, personID, selectedPaymentMethod, chargeSummary, feeSummary, fundSplits, funds, amountCents, note, billingName, billingEmail, queryClient]);

    const handlePaymentError = useCallback((message) => {
        setPaymentUrl(null);
        setPayinConfigID(null);
        setProcessing(false);
        setPaymentError(message || 'Payment failed');
    }, []);

    const resetFlow = useCallback(() => {
        setSuccess(false);
        setPaymentTimedOut(false);
        setPaymentError(null);
        setChargeSummary(null);
        setPaymentUrl(null);
        setPayinConfigID(null);
        setProcessing(false);
        setAmountCents(0);
        setNote('');
        setBillingName('');
        setBillingEmail('');
        setBillingPostal('');
        const defaultFund = funds?.find((f) => f.isDefault) || funds?.[0];
        setFundSplits([{ fundID: defaultFund?.id, amount: 0 }]);
        setSelectedPaymentMethodID(null);
        getLocalPaymentMethods(orgHandle).then(setLocalMethods);
        if (isOwnOrg) {
            queryClient.invalidateQueries({ queryKey: QueryKeys.giving.paymentMethods(statusOrgID, personID) });
        }
    }, [funds, orgHandle, isOwnOrg, statusOrgID, personID, queryClient]);

    if (loading) {
        return (
            <View style={[styles.container, styles.center]} testID="public-give-loading">
                <ActivityIndicator color={colors.giving} />
            </View>
        );
    }

    const isAvailable = status?.enableGiving && status?.merchantStatus === 'ACTIVE';
    if (!isAvailable) {
        return (
            <View style={[styles.container, styles.center]} testID="public-give-unavailable">
                <Unicon name="card-outline" size={48} colorVariant="textTertiary" />
                <Text style={styles.unavailableTitle}>This organization isn&apos;t accepting online gifts right now.</Text>
            </View>
        );
    }

    if (success) {
        return (
            <View style={[styles.container, { backgroundColor: appearance.backgroundColor }]}>
                <PatternBackground color={appearance.baseColor} />
                <ScrollView style={styles.scroll} contentContainerStyle={styles.successContent} testID="public-give-success">
                    <View style={styles.successHeader}>
                        <Unicon name="checkmark-circle" size={64} colorVariant="success" />
                        <Text style={[styles.successTitle, { color: appearance.baseColor }]}>Thank you!</Text>
                        <Text style={[styles.successBody, { color: appearance.textColor }]}>Your gift has been received.</Text>
                    </View>
                    {receiptData && (
                        <GivingReceipt
                            receiptData={receiptData}
                            organizationID={status?.organizationID}
                            personEmail={billingEmail}
                        />
                    )}
                    <View style={styles.successActions}>
                        <SolidButton testID="public-give-again" text="Give again" iconName="heart-outline" type="giving" customColor={appearance.baseColor} customTextColor={appearance.buttonTextColor} onPress={resetFlow} />
                    </View>
                </ScrollView>
            </View>
        );
    }

    if (paymentTimedOut) {
        return (
            <View style={[styles.container, { backgroundColor: appearance.backgroundColor }]}>
                <PatternBackground color={appearance.baseColor} />
                <ScrollView style={styles.scroll} contentContainerStyle={styles.successContent} testID="public-give-payment-timeout">
                    <View style={styles.successHeader}>
                        <Unicon name="time-outline" size={64} colorVariant="textTertiary" />
                        <Text style={[styles.successTitle, { color: appearance.baseColor }]}>Payment not confirmed</Text>
                        <Text style={[styles.successBody, { color: appearance.textColor }]}>{isOwnOrg
                            ? "We couldn't confirm whether your gift was processed. Check the Giving tab on your profile or contact your organization before trying again to avoid a duplicate gift."
                            : "We couldn't confirm whether your gift was processed. Contact the organization before trying again to avoid a duplicate gift."}</Text>
                    </View>
                    <View style={styles.successActions}>
                        <SolidButton testID="public-give-timeout-done" text="Done" type="giving" customColor={appearance.baseColor} customTextColor={appearance.buttonTextColor} onPress={resetFlow} />
                    </View>
                </ScrollView>
            </View>
        );
    }

    if (paymentUrl && selectedPaymentMethodID) {
        return (
            <View style={[styles.container, { backgroundColor: appearance.backgroundColor }]}>
                <PatternBackground color={appearance.baseColor} />
                <ScrollView style={styles.scroll} contentContainerStyle={styles.paymentContent} testID="public-give-payment">
                    <Text style={[styles.paymentHeaderTitle, { color: appearance.textColor }]}>Payment</Text>
                    <Text style={[styles.paymentHeaderSubtitle, { color: `${appearance.textColor}B3` }]}>Confirming your gift</Text>
                    <View style={[styles.paymentWebviewCard, { backgroundColor: appearance.surfaceColor }]}>
                        <PaymentWebView
                            url={paymentUrl}
                            onError={handlePaymentError}
                            style={styles.paymentWebview}
                        />
                        {processing && (
                            <View style={[styles.processingOverlay, { backgroundColor: appearance.surfaceColor }]} testID="public-give-processing">
                                <ActivityIndicator color={appearance.baseColor} size="large" />
                                <Text style={[styles.processingText, { color: appearance.textColor }]}>Processing your gift...</Text>
                                <Text style={[styles.processingSubtext, { color: `${appearance.textColor}B3` }]}>This may take a moment</Text>
                            </View>
                        )}
                    </View>
                </ScrollView>
            </View>
        );
    }

    return (
        <View style={[styles.container, { backgroundColor: appearance.backgroundColor }]}>
            <PatternBackground color={appearance.baseColor} />
            <ScrollView style={styles.scroll} contentContainerStyle={styles.content} testID="public-give-screen">
                <View style={styles.column}>
                    {appearance.logoPosition === 'long' && logoUrl && (
                        <View style={styles.longLogoContainer} testID="public-give-long-logo">
                            <Image source={{ uri: logoUrl }} style={{ width: 320, maxWidth: '100%', height: 72 }} contentFit="contain" transition={250} />
                        </View>
                    )}
                    <GivingHero
                        testID="public-give-hero"
                        orgName={status?.name || orgHandle}
                        logoUrl={logoUrl || null}
                        showIdentity={appearance.logoPosition === 'square'}
                        baseColor={appearance.baseColor}
                        gradientColors={appearance.gradientColors}
                        heroTextColor={appearance.heroTextColor}
                        surfaceColor={appearance.surfaceColor}
                        amountCents={amountCents}
                        onAmountChange={handleAmountChange}
                        amountTestID="amount-input"
                    />

                    <View testID="public-give-fund-splits-section" pointerEvents={hasAmount ? 'auto' : 'none'} style={!hasAmount && styles.disabledSection}>
                        <GivingCard surfaceColor={appearance.surfaceColor} textColor={appearance.textColor}>
                            <FundSplitsEditor
                                testID="public-give-fund-splits"
                                amountCents={amountCents}
                                fundSplits={fundSplits}
                                funds={funds || []}
                                onChange={setFundSplits}
                                appearance={appearance}
                            />
                        </GivingCard>
                    </View>

                    <GivingCard surfaceColor={appearance.surfaceColor} textColor={appearance.textColor}>
                        <PaymentMethodPicker
                            testID="public-give-payment-method"
                            methods={paymentMethods}
                            selectedPaymentMethodID={selectedPaymentMethodID}
                            onSelect={handleSelectPaymentMethod}
                            onAdd={() => setShowAddMethod(true)}
                            appearance={appearance}
                        />
                    </GivingCard>

                    <GivingCard testID="public-give-billing" title="Billing contact" surfaceColor={appearance.surfaceColor} textColor={appearance.textColor}>
                        <TextInput
                            testID="public-give-billing-name"
                            style={[styles.textInput, { backgroundColor: appearance.inputColor, color: appearance.textColor }]}
                            placeholder="Name"
                            placeholderTextColor={`${appearance.textColor}80`}
                            value={billingName}
                            onChangeText={setBillingName}
                        />
                        <TextInput
                            testID="public-give-billing-email"
                            style={[styles.textInput, { backgroundColor: appearance.inputColor, color: appearance.textColor }]}
                            placeholder="Email"
                            placeholderTextColor={`${appearance.textColor}80`}
                            keyboardType="email-address"
                            autoCapitalize="none"
                            value={billingEmail}
                            onChangeText={setBillingEmail}
                        />
                        <TextInput
                            testID="public-give-billing-postal"
                            style={[styles.textInput, { backgroundColor: appearance.inputColor, color: appearance.textColor }]}
                            placeholder="Postal code"
                            placeholderTextColor={`${appearance.textColor}80`}
                            value={billingPostal}
                            onChangeText={setBillingPostal}
                        />
                    </GivingCard>

                    <GivingCard surfaceColor={appearance.surfaceColor} textColor={appearance.textColor}>
                        <NoteInput collapsible testID="public-give-note" value={note} onChange={setNote} appearance={appearance} />
                    </GivingCard>

                    {paymentError && (
                        <View style={styles.errorCard} testID="public-give-payment-error">
                            <Text style={styles.errorText}>{paymentError}</Text>
                        </View>
                    )}

                    <View style={styles.actionArea}>
                        <SolidButton
                            testID="public-give-submit"
                            text={submitButtonText}
                            iconName="heart-outline"
                            type="giving"
                            size="lg"
                            disabled={!!givingDisabledReason}
                            processing={creating || processing}
                            customColor={appearance.baseColor}
                            customTextColor={appearance.buttonTextColor}
                            onPress={handleGive}
                        />
                        {givingDisabledReason && <Text style={styles.disabledReason}>{givingDisabledReason}</Text>}
                        <FeeCoverageToggle allowed={status?.allowGiversToChooseCoverFees} value={coverFees} onChange={(value) => { setCoverFees(value); setReviewedCharge(null); }} disabled={creating || processing} />
                        {feeNotice && (
                            <Text style={[styles.feeNotice, { color: `${appearance.textColor}B3` }]} testID="public-give-fee-notice">{feeNotice}</Text>
                        )}
                    </View>
                </View>
                <AddPaymentMethodModal
                    isOpen={showAddMethod}
                    onClose={() => setShowAddMethod(false)}
                    onPaymentMethodAdded={handlePaymentMethodAdded}
                    organizationID={statusOrgID}
                    personID={personID}
                    orgHandle={orgHandle}
                    billingContact={billingContact}
                    authenticated={isOwnOrg}
                />
            </ScrollView>
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, spacing, roundness, shadows, typography }, rt) => ({
    container: {
        flex: 1,
        backgroundColor: colors.background,
    },
    scroll: {
        flex: 1,
        backgroundColor: 'transparent',
    },
    paymentContent: {
        padding: spacing.md,
        gap: spacing.md,
        alignItems: 'center',
        _web: {
            maxWidth: 500,
            alignSelf: 'center',
            width: '100%',
            paddingVertical: spacing.xl,
        },
    },
    paymentHeaderTitle: {
        fontSize: typography.h1,
        fontFamily: fonts.heading,
        color: colors.text,
        textAlign: 'center',
    },
    paymentHeaderSubtitle: {
        fontSize: typography.body,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textAlign: 'center',
    },
    paymentWebviewCard: {
        width: '100%',
        borderRadius: roundness.md,
        backgroundColor: colors.surface,
        boxShadow: shadows.card,
        overflow: 'hidden',
        minHeight: 400,
    },
    paymentWebview: {
        height: 400,
    },
    processingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        alignItems: 'center',
        justifyContent: 'center',
        gap: spacing.md,
        backgroundColor: colors.surface,
    },
    processingText: {
        fontSize: typography.body,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    processingSubtext: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textTertiary,
    },
    content: {
        padding: spacing.md,
        // Header is hidden — on mobile, clear the notch/status bar and keep
        // content off the top edge. Desktop keeps the standard padding.
        paddingTop: rt.breakpoint === 'xs' ? rt.insets.top + spacing.xl : spacing.md,
        _web: {
            padding: spacing.md,
            paddingTop: rt.breakpoint === 'xs' ? rt.insets.top + spacing.xl : spacing.md,
            paddingBottom: spacing.xl,
        },
    },
    // Hero and form share the same centered column (max 600 on web).
    column: {
        gap: spacing.md,
        _web: {
            maxWidth: 600,
            alignSelf: 'center',
            width: '100%',
        },
    },
    actionArea: {
        gap: spacing.sm,
    },
    disabledSection: {
        opacity: 0.5,
    },
    longLogoContainer: {
        minHeight: 72,
        paddingHorizontal: spacing.md,
        alignItems: 'center',
        justifyContent: 'center',
    },
    feeNotice: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textAlign: 'center',
        lineHeight: 18,
    },
    disabledReason: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.error,
        textAlign: 'center',
    },
    center: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        gap: spacing.md,
        padding: spacing.xl,
    },
    successContent: {
        padding: spacing.md,
        gap: spacing.lg,
        paddingBottom: spacing.xxl + 60,
        _web: {
            maxWidth: 600,
            alignSelf: 'center',
            width: '100%',
            paddingBottom: spacing.xl,
        },
    },
    successHeader: {
        alignItems: 'center',
        gap: spacing.xs,
        paddingVertical: spacing.md,
    },
    successActions: {
        gap: spacing.sm,
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'center',
    },
    textInput: {
        borderRadius: roundness.sm,
        backgroundColor: colors.surfaceVariant,
        color: colors.text,
        fontFamily: fonts.body,
        fontSize: typography.body,
        padding: spacing.md,
        marginVertical: spacing.xs,
    },
    unavailableTitle: {
        fontSize: typography.h2,
        fontFamily: fonts.heading,
        color: colors.text,
        textAlign: 'center',
    },
    successTitle: {
        fontSize: typography.h1,
        fontFamily: fonts.heading,
        color: colors.giving,
    },
    successBody: {
        fontSize: typography.body,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textAlign: 'center',
    },
    errorCard: {
        padding: spacing.md,
        borderRadius: roundness.sm,
        backgroundColor: colors.errorContainer,
    },
    errorText: {
        fontSize: typography.body,
        fontFamily: fonts.body,
        color: colors.error,
        textAlign: 'center',
    },
}));
